import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { DataSourceOrdenacaoInsensivel } from '../../../../shared/helpers/ordenacao-tabela.helper';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSort, MatSortHeader, MatSortModule } from '@angular/material/sort';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { Router, RouterModule } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { EmissorService } from '../../../../shared/services/apis/dfe/emissor.service';
import { GetAllEmissorResponse } from '../../../../shared/services/responses/dfe/get-all-emissor.response';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { ReturnStruct } from '../../../../shared/services/structs/return.struct';
import { EmptyListComponent } from '../../../../shared/components/empty-list/empty-list.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { ListaEmissorStruct } from '../../../../shared/services/structs/dfe/lista-emissor.struct';
import { UtilService } from '../../../../shared/services/util.service';
import { MatMenuModule } from '@angular/material/menu';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LoadingBoxDefaultListComponent } from '../../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import {
  ConfiguracaoColunasModalComponent,
  ColunaConfiguracao,
} from '../../../../shared/components/configuracao-colunas-modal/configuracao-colunas-modal.component';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { ModeloDocumentoEnum } from '@shared/enums/dfe/modelo-documento.enum';
import { TipoAmbienteEnum } from '@shared/enums/dfe/tipo-ambiente.enum';
import { NaturezaOperacaoService } from '@shared/services/apis/motor-fiscal/natureza-operacao.service';
import { AuthService } from '@shared/services/apis/usuario/auth.service';
import { PermissaoEnum } from '@shared/enums/usuario/permissao.enum';
import {
  ReatribuirNaturezasEmissorDialogComponent,
  ReatribuirNaturezasEmissorDialogData,
} from './dialogs/reatribuir-naturezas-emissor-dialog/reatribuir-naturezas-emissor-dialog.component';

@Component({
  selector: 'app-emissor-lista',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    EmptyListComponent,
    MatMenuModule,
    MatSortModule,
    MatSort,
    MatSortHeader,
    HeaderComponent,
    LoadingBoxDefaultListComponent,
    AlertCardComponent,
  ],
  templateUrl: './emissor-lista.component.html',
  styleUrls: ['./emissor-lista.component.css'],
})
export class EmissorListaComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_NotasFiscais;
  public model: FormGroup;
  public _isLoading = false;
  public _ultimaPagina = false;
  public _paginaAtual = 0;
  public listaEmissoresResponse: GetAllEmissorResponse | undefined;
  public _dataSource = new DataSourceOrdenacaoInsensivel<ListaEmissorStruct>();
  public displayedColumns: string[] = [];
  public readonly TipoAmbienteEnum = TipoAmbienteEnum;
  /** `false` só quando a consulta confirmou que nenhum emissor é o padrão para NF-e; `null` enquanto não se sabe. */
  public possuiEmissorPadrao: boolean | null = null;
  public podeAlterar = false;
  public podeExcluir = false;
  public readonly tooltipSemPermissaoExcluir = 'Seu perfil não tem permissão para excluir emissores.';

  // Columns Definition
  public todosCampos: ColunaConfiguracao[] = [
    { def: 'nome', label: 'Nome', visible: true, fixed: false },
    { def: 'serieSequencialNFCe', label: 'Seq./Série NFC-e', visible: true, fixed: false },
    { def: 'serieSequencialNFe', label: 'Seq./Série NF-e', visible: true, fixed: false },
    { def: 'tipoAmbiente', label: 'Tipo ambiente', visible: true, fixed: false },
    { def: 'padrao', label: 'Padrão', visible: true, fixed: false },
    { def: 'token', label: 'Token', visible: true, fixed: false },
    { def: 'ativo', label: 'Status', visible: true, fixed: false },
    { def: 'actions', label: 'Ações', visible: true, fixed: true, pinnedEnd: true },
  ];

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this._dataSource.sort = sort;
  }

  constructor(
    private formBuilder: FormBuilder,
    private emissorService: EmissorService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private router: Router,
    private utilService: UtilService,
    private naturezaOperacaoService: NaturezaOperacaoService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    const permissoes = this.authService.getUserInfos()?.listaPermissoes ?? [];
    this.podeAlterar = permissoes.includes(PermissaoEnum.Configuracoes_NotasFiscais_Alterar);
    this.podeExcluir = permissoes.includes(PermissaoEnum.Configuracoes_NotasFiscais_Excluir);

    this.model = this.formBuilder.group({
      pesquisa: [''],
    });

    this.loadFilters();
    this.atualizarColunasVisiveis();
    this.pesquisar();
    this.carregarSituacaoPadrao();
  }
  ngOnDestroy(): void {
    this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, [
      '/fiscal/emissor/cadastrar',
    ]);
  }

  private loadFilters() {
    const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
    if (filtroSalvo) {
      this.model.patchValue(filtroSalvo);
    }
  }

  clickPesquisar(): void {
    this._paginaAtual = 0;
    this.listaEmissoresResponse = undefined;
    this._dataSource.data = [];
    this.pesquisar();
  }

  pesquisar(): void {
    if (this._isLoading) {
      return;
    }

    this._isLoading = true;
    const pesquisa = this.model.get('pesquisa')?.value || '';

    this.emissorService.getAll(pesquisa, this._paginaAtual).subscribe({
      next: response => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          return;
        }

        if (!this.listaEmissoresResponse) {
          this.listaEmissoresResponse = response;
        } else {
          this.listaEmissoresResponse.listaEmissorStruct.push(...response.listaEmissorStruct);
        }

        this._dataSource.data = this.listaEmissoresResponse.listaEmissorStruct;
        // A API pagina de 50 em 50.
        this._ultimaPagina = response.listaEmissorStruct.length < 50;
      },
      error: error => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
      },
    });
  }

  proximaPagina(): void {
    this._paginaAtual++;
    this.pesquisar();
  }

  /**
   * A listagem pode estar filtrada ou paginada, então quem responde se existe emissor padrão é
   * a lista de elegíveis de NF-e, que traz todos os emissores ativos com NF-e ativa.
   */
  private carregarSituacaoPadrao(): void {
    this.emissorService.getElegiveis(ModeloDocumentoEnum.NFe).subscribe({
      next: response => {
        this.possuiEmissorPadrao = response.isError
          ? null
          : (response.listaEmissorElegivel ?? []).some(emissor => emissor.isPadraoNFe);
      },
      error: () => (this.possuiEmissorPadrao = null),
    });
  }

  /** Motivo de "Definir padrão NF-e" estar indisponível para a linha, ou null quando disponível. */
  public motivoDefinirPadraoIndisponivel(emissor: ListaEmissorStruct): string | null {
    if (!this.podeAlterar) return 'Seu perfil não tem permissão para alterar emissores.';
    if (emissor.isPadraoNFe) return 'Este já é o emissor padrão para NF-e.';
    if (!emissor.emiteNFe) return 'Disponível apenas para emissores com a emissão de NF-e ativa.';
    if (!emissor.ativo) return 'Disponível apenas para emissores ativos.';
    return null;
  }

  public definirPadraoNFe(emissor: ListaEmissorStruct): void {
    if (this.motivoDefinirPadraoIndisponivel(emissor)) return;

    this.emissorService.definirPadraoNFe(emissor.idEmissor).subscribe({
      next: response => {
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          return;
        }
        const mensagem = response.nomeEmissorAnterior
          ? `"${response.nomeEmissorNovo}" passou a ser o emissor padrão para NF-e no lugar de "${response.nomeEmissorAnterior}".`
          : `"${response.nomeEmissorNovo}" passou a ser o emissor padrão para NF-e.`;
        this.alertService.show('Sucesso', mensagem, AlertType.success);
        this.clickPesquisar();
        this.carregarSituacaoPadrao();
      },
      error: error => {
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
      },
    });
  }

  /**
   * O emissor padrão só sai depois que outro for marcado. Fora isso, naturezas que usam o
   * emissor precisam ser reatribuídas antes: o dialog de reatribuição já serve de confirmação.
   */
  abrirModalExcluir(emissor: ListaEmissorStruct): void {
    if (!this.podeExcluir) return;
    if (emissor.isPadraoNFe) {
      this.alertService.show(
        'Atenção',
        'Marque outro emissor como padrão para NF-e antes de excluir este emissor.',
        AlertType.warning
      );
      return;
    }

    this.naturezaOperacaoService.getPorEmissor(emissor.idEmissor).subscribe({
      next: response => {
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          return;
        }

        const naturezas = response.naturezas ?? [];
        if (naturezas.length === 0) {
          this.confirmarExclusao(emissor);
          return;
        }

        const dialogRef = this.dialog.open<ReatribuirNaturezasEmissorDialogComponent, ReatribuirNaturezasEmissorDialogData, boolean>(
          ReatribuirNaturezasEmissorDialogComponent,
          {
            width: '640px',
            maxWidth: '95vw',
            data: {
              idEmissor: emissor.idEmissor,
              nomeEmissor: emissor.nome,
              descricaoAcao: 'excluir este emissor',
              naturezas,
            },
          }
        );
        dialogRef.afterClosed().subscribe(reatribuiu => {
          if (reatribuiu) this.excluirEmissor(emissor.idEmissor);
        });
      },
      error: () => {
        this.alertService.show(
          'Erro inesperado',
          'Não foi possível verificar as naturezas de operação que usam este emissor.',
          AlertType.error
        );
      },
    });
  }

  private confirmarExclusao(emissor: ListaEmissorStruct): void {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Confirmar Exclusão',
        description: `Você tem certeza que deseja excluir o emissor "${emissor.nome}"? Esta ação é irreversível e qualquer integração que utiliza este emissor irá parar de emitir notas instantaneamente.`,
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.confirm) {
        this.excluirEmissor(emissor.idEmissor);
      }
    });
  }

  private atualizarColunasVisiveis(): void {
    this.displayedColumns = this.todosCampos.filter(c => c.visible).map(c => c.def);
  }

  public configurarColunas(): void {
    const dialogRef = this.dialog.open(ConfiguracaoColunasModalComponent, {
      maxWidth: '700px',
      data: { colunas: this.todosCampos },
    });

    dialogRef.afterClosed().subscribe((result: ColunaConfiguracao[]) => {
      if (result) {
        this.todosCampos = result;
        this.atualizarColunasVisiveis();
      }
    });
  }

  excluirEmissor(idEmissor: number): void {
    this.emissorService.delete(idEmissor).subscribe({
      next: (response: ReturnStruct) => {
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
        } else {
          this.alertService.show('Sucesso', 'Emissor excluído com sucesso.', AlertType.success);
          this.clickPesquisar();
        }
      },
      error: error => {
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
      },
    });
  }

  selecionarToken(event: MouseEvent): void {
    const el = event.target as HTMLElement;
    const range = document.createRange();
    range.selectNodeContents(el);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }

  voltar() {
    this.router.navigate(['/configuracoes/notas-fiscais']);
  }
}
